import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  API_BASE_URL,
  REPORT_FEE_INR,
  GST_RATE,
  REPORT_TOTAL_INR,
  LEAD_KEY,
  RECORDS_KEY,
  CTX_KEY,
  customerFetch,
  leadSaveBackoffMs,
  persistSession,
  setStoredCustomerToken,
  sleepMs,
} from '../api/config';
import LeadModal from '../components/LeadModal';
import { PageShell } from '../components/Layout';
import { IconArrowLeft, IconLock, IconSearch, IconSpinner } from '../components/Icons';
import { buildSearchUrl, dedupeRecords, formatPartialDate, matchesPropertyType } from '../utils/records';

export default function ResultsPage() {
  const navigate = useNavigate();
  const checkoutRef = useRef(null);
  const [searchParams] = useSearchParams();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPaying, setIsPaying] = useState(false);
  const [payingPhase, setPayingPhase] = useState('');
  const [paymentError, setPaymentError] = useState(null);
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [idLabels, setIdLabels] = useState({ de: '', te: '', ve: '' });

  const dParam = searchParams.get('d');
  const tParam = searchParams.get('t');
  const vParam = searchParams.get('v');
  const de = searchParams.get('de') || '';
  const te = searchParams.get('te') || '';
  const ve = searchParams.get('ve') || '';
  const district = searchParams.get('district') || '';
  const taluka = searchParams.get('taluka') || '';
  const village = searchParams.get('village') || '';
  const query = searchParams.get('query');
  const propertyType = searchParams.get('propertyType') || 'land';

  const locLabelD = de || idLabels.de || district;
  const locLabelT = te || idLabels.te || taluka;
  const locLabelV = ve || idLabels.ve || village;

  useEffect(() => {
    if (de && te && ve) {
      setIdLabels({ de: '', te: '', ve: '' });
      return;
    }
    if (!dParam || !tParam || !vParam) return;
    let cancelled = false;
    fetch(`${API_BASE_URL}/locations`)
      .then((r) => r.json())
      .then((data) => {
        if (cancelled || !data.districts) return;
        const dRow = data.districts.find((x) => String(x.id) === String(dParam));
        const tRow = dRow?.talukas?.find((x) => String(x.id) === String(tParam));
        const vRow = tRow?.villages?.find((x) => String(x.id) === String(vParam));
        if (dRow && tRow && vRow) setIdLabels({ de: dRow.en, te: tRow.en, ve: vRow.en });
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [dParam, tParam, vParam, de, te, ve]);

  useEffect(() => {
    setLoading(true);
    const url = buildSearchUrl(API_BASE_URL, {
      dParam, tParam, vParam, district, taluka, village, query,
    });
    fetch(url)
      .then((res) => res.json())
      .then((data) => {
        if (data.error) setError(data.error);
        else setRecords(dedupeRecords(data.results));
        setTimeout(() => setLoading(false), 600);
      })
      .catch(() => {
        setError('Failed to fetch records');
        setLoading(false);
      });
  }, [dParam, tParam, vParam, district, taluka, village, query]);

  const filteredRecords = records.filter((r) => matchesPropertyType(r, propertyType));

  const handleProceedToPay = async () => {
    setPaymentError(null);
    try {
      const meRes = await customerFetch(`${API_BASE_URL}/customer/session/me`);
      if (meRes.ok) {
        const me = await meRes.json().catch(() => ({}));
        const customer = me?.customer;
        if (customer?.email && customer?.phone) {
          const knownLead = { email: customer.email, phone: customer.phone };
          persistSession(LEAD_KEY, knownLead);
          initiatePayment(knownLead);
          return;
        }
      }
    } catch (_) {}
    setShowLeadModal(true);
  };

  const handleLeadSubmit = async (leadData) => {
    setShowLeadModal(false);
    persistSession(LEAD_KEY, leadData);
    await initiatePayment(leadData);
  };

  const initiatePayment = async (leadData) => {
    setPaymentError(null);
    setPayingPhase('saving');
    setIsPaying(true);
    try {
      const ctx = {
        d: dParam, t: tParam, v: vParam,
        de, te, ve,
        district, taluka, village,
        query,
      };
      persistSession(RECORDS_KEY, filteredRecords);
      persistSession(CTX_KEY, ctx);

      const AMOUNT = REPORT_TOTAL_INR.toFixed(2);
      const txnid = `KDV_${Date.now()}_${Math.random().toString(36).substr(2, 6).toUpperCase()}`;
      const leadPayload = {
        email: leadData.email,
        phone: leadData.phone,
        district: locLabelD || district,
        taluka: locLabelT || taluka,
        village: locLabelV || village,
        query,
        txnid,
        selectedKeys: filteredRecords.map((r) => r?.key).filter(Boolean),
        d: dParam,
        t: tParam,
        v: vParam,
        de, te, ve,
        propertyType,
      };

      let leadJson = {};
      let attempt = 0;
      for (;;) {
        let leadRes;
        try {
          leadRes = await customerFetch(`${API_BASE_URL}/leads`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(leadPayload),
          });
        } catch (e) {
          await sleepMs(leadSaveBackoffMs(attempt++));
          continue;
        }
        leadJson = await leadRes.json().catch(() => ({}));
        if (leadRes.ok) break;
        if (leadRes.status === 400) throw new Error(leadJson.error || 'Could not validate contact details.');
        await sleepMs(leadSaveBackoffMs(attempt++));
      }

      if (leadJson?.customerToken) setStoredCustomerToken(leadJson.customerToken);

      setPayingPhase('checkout');
      const res = await fetch(`${API_BASE_URL}/payu/initiate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          amount: AMOUNT,
          productinfo: `Property Report - ${query}`,
          firstname: 'Customer',
          email: leadData.email,
          phone: leadData.phone,
          searchQuery: query,
          txnid,
        }),
      });
      if (!res.ok) throw new Error('Could not initiate payment');
      const params = await res.json();

      const form = document.createElement('form');
      form.method = 'POST';
      form.action = params.action;
      ['key', 'txnid', 'amount', 'productinfo', 'firstname', 'email', 'phone', 'surl', 'furl', 'hash', 'udf1'].forEach((field) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = field;
        input.value = params[field] || '';
        form.appendChild(input);
      });
      document.body.appendChild(form);
      form.submit();
    } catch (err) {
      setPaymentError(err.message || 'Something went wrong.');
      setIsPaying(false);
      setPayingPhase('');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-parchment flex flex-col items-center justify-center gap-4">
        <IconSpinner className="w-10 h-10 text-forest" />
        <p className="font-serif text-xl text-forest">Searching registry…</p>
      </div>
    );
  }

  const showMobileBar = !error && filteredRecords.length > 0;

  return (
    <PageShell className={showMobileBar ? 'pb-24 md:pb-0' : ''}>
      <LeadModal isOpen={showLeadModal} onClose={() => setShowLeadModal(false)} onSubmit={handleLeadSubmit} />

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 md:py-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 pb-6 border-b-2 border-sand">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-clay mb-2">Search results</p>
            <h1 className="font-serif text-3xl md:text-4xl text-forest font-semibold">
              {filteredRecords.length} record{filteredRecords.length !== 1 ? 's' : ''} found
            </h1>
            <p className="text-sm text-ink/60 mt-2 font-medium">
              {locLabelD} → {locLabelT} → {locLabelV} · Property #{query}
            </p>
          </div>
          <button type="button" onClick={() => navigate('/')} className="btn-outline self-start">
            <IconArrowLeft className="w-4 h-4" />
            New search
          </button>
        </div>

        {error && (
          <div className="border-2 border-clay/30 bg-clay/5 text-clay px-6 py-4 font-semibold">{error}</div>
        )}

        {!error && filteredRecords.length === 0 && (
          <div className="text-center py-20 border-2 border-dashed border-sand">
            <IconSearch className="w-12 h-12 text-sand mx-auto mb-4" />
            <h2 className="font-serif text-2xl text-forest">No matching records</h2>
            <p className="text-ink/60 mt-2 text-sm">Try a different property number or location.</p>
          </div>
        )}

        {filteredRecords.length > 0 && (
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="border-2 border-forest overflow-hidden">
                <div className="grid grid-cols-12 bg-forest text-white text-xs font-bold uppercase tracking-wider">
                  <div className="col-span-5 px-4 py-3">Document type</div>
                  <div className="col-span-4 px-4 py-3">Doc number</div>
                  <div className="col-span-3 px-4 py-3">Date</div>
                </div>
                {filteredRecords.map((record, i) => {
                  const datePart = formatPartialDate(record.date);
                  return (
                    <div
                      key={record.key || i}
                      className={`grid grid-cols-12 text-sm border-t border-sand ${i % 2 === 0 ? 'bg-white' : 'bg-parchment/50'}`}
                    >
                      <div className="col-span-5 px-4 py-4 font-semibold text-forest">
                        {record.document_type || 'Record'}
                      </div>
                      <div className="col-span-4 px-4 py-4 font-mono text-ink/40 blur-sm select-none">
                        12345/2019
                      </div>
                      <div className="col-span-3 px-4 py-4 text-ink/60">
                        {datePart ? (
                          <span>
                            {datePart}/
                            <span className="blur-sm select-none">2019</span>
                          </span>
                        ) : '—'}
                      </div>
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-ink/50 mt-3">
                Full document numbers and details unlock after payment.
              </p>
            </div>

            <div ref={checkoutRef} className="lg:col-span-1">
              <div className="bg-white border-2 border-forest shadow-stamp p-6 sticky top-24">
                <h3 className="font-serif text-xl text-forest font-semibold">Order summary</h3>
                <div className="mt-5 space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-ink/60">Report fee</span>
                    <span className="font-semibold">₹{REPORT_FEE_INR}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-ink/60">GST (18%)</span>
                    <span className="font-semibold">₹{Number((REPORT_FEE_INR * GST_RATE).toFixed(2))}</span>
                  </div>
                  <div className="border-t-2 border-sand pt-3 flex justify-between text-lg font-bold text-forest">
                    <span>Total</span>
                    <span>₹{REPORT_TOTAL_INR}</span>
                  </div>
                </div>
                <p className="text-xs text-ink/50 mt-4 leading-relaxed">
                  Includes all {filteredRecords.length} matched records in a downloadable HTML report.
                </p>
                {paymentError && (
                  <p className="text-clay text-xs font-semibold mt-3">{paymentError}</p>
                )}
                <button
                  type="button"
                  onClick={handleProceedToPay}
                  disabled={isPaying}
                  className="btn-primary w-full mt-5 disabled:opacity-60"
                >
                  {isPaying ? (
                    <>
                      <IconSpinner className="w-5 h-5" />
                      {payingPhase === 'checkout' ? 'Opening checkout…' : 'Saving details…'}
                    </>
                  ) : (
                    <>
                      <IconLock className="w-5 h-5" />
                      Pay ₹{REPORT_TOTAL_INR}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {showMobileBar && (
        <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 p-3 bg-white border-t-2 border-forest">
          <button
            type="button"
            onClick={() => checkoutRef.current?.scrollIntoView({ behavior: 'smooth' })}
            className="btn-primary w-full"
          >
            Pay ₹{REPORT_TOTAL_INR} — View summary
          </button>
        </div>
      )}
    </PageShell>
  );
}
